/* ────────────────────────────────────────────────────────────────────
   Creator's Friend — one build, one line in the cart.
   Loaded from layout/theme.liquid.

   WHAT IT DOES

   A finished folio is up to TEN cart lines: the folio, three laces, the
   embossing, the long press, the inserts and the charms. It has to be
   ten — a line item property cannot carry a price, so every component
   that costs money has to be a real line, which is also what makes
   stock decrement, sales apply and market pricing work. Ten lines are
   right. Showing ten rows is not.

   This groups them by the `_atelier` token every line of one build
   carries, hides the components, and rewrites the folio's row to stand
   for the whole build: one title, one price, one Remove that takes all
   ten, and a drop-down listing exactly what is in it, priced.

   It does the same for an OFF-THE-SHELF FOLIO WITH A MONOGRAM — two
   lines tied by the `_build` token the personalisation block writes
   onto both. That row keeps its properties on show, because the
   initials and the placement are the customer's own choices and there
   is nowhere else they appear.

   WHERE IT STOPS: Shopify's CHECKOUT still shows every line. Nothing a
   theme can do reaches it; merging them the whole way through needs a
   Cart Transform function, which is an app rather than theme code.

   WHY THIS IS AN ASSET AND NOT A CUSTOM CODE BLOCK

   It lived in two Custom code blocks — one on the Cart section, one on
   the Cart drawer — because those are real theme blocks and survive a
   Broadcast update. On the cart page it never ran. Measured on the live
   site, on a cart holding a complete build: no script, no style, not
   even the text of either anywhere in the page. The block is saved with
   both intact; the page renders neither.

   A `liquid` setting is a text field Shopify parses and normalises on
   the way in and out — the documentation says as much about unclosed
   tags — and it is not a dependable host for a stylesheet or a script.
   Rather than keep guessing at the mechanism, this moved to where
   site-wide behaviour belongs.

   It is also now ONE copy instead of two that could drift apart.

   THE COST, STATED PLAINLY: layout/theme.liquid is a theme file, so a
   Broadcast update will wipe the three lines that load this. They are
   three lines and they are in the runbook. The blocks were the thing
   that avoided that, and they did not work.
   ──────────────────────────────────────────────────────────────────── */

(function(){
  "use strict";
  if (window.__atlCart) return;
  window.__atlCart = true;

  var ROOT  = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  /* Handed in from layout/theme.liquid, because an asset file is not
     rendered through Liquid and the format is a shop setting rather
     than something to hardcode. The fallback is only reached if that
     line is missing, and it is the Australian default. */
  var MONEY = window.__ATL_MONEY || "${{amount}}";
  var ADDON = "monogram-debossing";   // the off-the-shelf add-on, left alone

  /* Shopify's money_format is a template, not a format string, and the
     one this shop uses is whatever is set in Settings. Render it from
     Liquid and fill it, rather than hardcoding a dollar sign that would
     be wrong the moment someone looks at the store from another market. */
  function money(cents){
    var v = (cents / 100);
    function commas(n, dp){
      var s = n.toFixed(dp).split(".");
      s[0] = s[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return s.join(".");
    }
    return MONEY
      .replace(/\{\{\s*amount\s*\}\}/g, commas(v, 2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, commas(v, 0))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g,
               commas(v, 2).replace(/,/g, "|").replace(/\./g, ",").replace(/\|/g, "."))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
               commas(v, 0).replace(/,/g, "."));
  }

  function esc(s){
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function getCart(){
    return fetch(ROOT + "cart.js", { headers: { Accept: "application/json" } })
      .then(function(r){ return r.json(); });
  }

  /* TWO THINGS ARRIVE AS MORE THAN ONE LINE, AND BOTH BELONG TOGETHER.

     An ATELIER build carries `_atelier` on every line of it, and the
     folio is the one that also carries `_spec`.

     An OFF-THE-SHELF FOLIO WITH A MONOGRAM is two lines carrying the
     same `_build` token — the folio and the debossing charge — written
     by the personalisation block at the moment the form submits. Here
     the parent is simply the line that is NOT the charge.

     Same shape either way, so everything downstream — the folded rows,
     the single Remove, the orphan sweep — works on both without knowing
     which it has. The two tokens cannot collide: an atelier folio never
     carries `_build`, and an off-the-shelf one never carries `_atelier`.

     Left alone: a monogram charge with no `_build` at all. That is an
     older line from before the token existed, and guessing which folio
     it belongs to by reading initials across two rows is exactly the
     kind of cleverness that gets someone the wrong thing. */
  function groupsIn(cart){
    var by = {}, order = [];

    function bucket(tok, kind){
      if (!by[tok]){
        by[tok] = { token: tok, kind: kind, lines: [], parent: null, total: 0 };
        order.push(by[tok]);
      }
      return by[tok];
    }

    (cart.items || []).forEach(function(it){
      var props = it.properties || {}, g = null;

      if (props._atelier && it.handle !== ADDON){
        g = bucket(props._atelier, "atelier");
        if (props._spec) g.parent = it;
      } else if (props._build){
        g = bucket(props._build, "monogram");
        if (it.handle !== ADDON) g.parent = it;   // the folio, not the charge
      }
      if (!g) return;

      g.lines.push(it);
      g.total += it.final_line_price;
    });
    return order;
  }

  function rowFor(key){ return document.querySelector('.cart__item[data-item="' + key + '"]'); }

  function paint(){
    return getCart().then(function(cart){
      var groups = groupsIn(cart), collapsed = 0;

      groups.forEach(function(g){
        // A group with no folio line is an orphan, not a build. Leave it
        // visible: the customer should see what they are being charged
        // for, and the sweep below is what tidies it.
        if (!g.parent || g.lines.length < 2) return;

        var host = rowFor(g.parent.key);
        if (!host) return;

        /* QUANTITIES, not lines. A build with three lunar charms is ten
           lines but twelve items, and subtracting nine of them would
           leave the header claiming three folios. What collapses into
           one row is everything the build contributes, less the one
           item it now reads as. */
        var units = 0;
        g.lines.forEach(function(it){ units += it.quantity; });
        collapsed += units - 1;

        g.lines.forEach(function(it){
          if (it.key === g.parent.key) return;
          var el = rowFor(it.key);
          if (el) el.classList.add("atl-hidden");
        });

        if (host.dataset.atlDone === g.token + ":" + g.total) return;
        host.dataset.atlDone = g.token + ":" + g.total;
        host.classList.add("atl-row");
        if (g.kind === "atelier") host.classList.add("atl-fold");

        /* Belt and braces. CSS hides the two quantity controls; this
           makes the field behind them refuse a change even if a rule
           ever fails to load. Runs again on every re-render, because
           the theme rebuilds the row from the server each time. */
        var qtyField = host.querySelector(".cart__quantity-field");
        if (qtyField) qtyField.readOnly = true;

        // ---- title -------------------------------------------------------
        var title = host.querySelector(".cart__item__title");
        if (title && !title.querySelector(".atl-tag"))
          title.insertAdjacentHTML("beforeend", '<span class="atl-tag">' +
            (g.kind === "atelier" ? "Atelier" : "Personalised") + '</span>');

        // ---- summary and the parts list ----------------------------------
        var meta = host.querySelector(".cart__item__meta");
        if (meta){
          var conf = (g.parent.properties || {}).Configuration || "";
          var parts = g.lines.map(function(it){
            var name = it.product_title +
              (it.variant_title && it.variant_title !== "Default Title" ? " — " + it.variant_title : "");
            return '<li><span>' + esc(name) + (it.quantity > 1 ? " ×" + it.quantity : "") +
                   '</span><b>' + money(it.final_line_price) + '</b></li>';
          }).join("");

          var old = meta.querySelector(".atl-block");
          if (old) old.remove();
          meta.insertAdjacentHTML("beforeend",
            '<div class="atl-block">' +
              (conf ? '<p class="cart__item__property atl-keep atl-sum">' + esc(conf) + '</p>' : '') +
              '<details class="atl-parts"><summary>' + g.lines.length +
                 (g.lines.length === 1 ? ' component' : ' components') + '</summary>' +
              '<ul>' + parts + '</ul></details>' +
              '<p class="atl-made atl-keep">Made to order — built one at a time.</p>' +
            '</div>');
        }

        /* ---- what the build saved -----------------------------------------

           The theme prints one discount row per LINE, and every line but
           the folio's is now hidden — so a build that saved $14.97 across
           four discounted components said nothing at all about it, while
           quietly charging the lower price.

           Totalled across the group and printed once, in the theme's own
           `.cart__discount` row so it looks like every other discount in
           the cart rather than something this script invented. The
           per-line rows that survive on the folio's own element are
           hidden, or its share would be counted twice — once in the total
           and once on its own. */
        var saved = 0, titles = [];
        g.lines.forEach(function(it){
          var was = (it.original_line_price == null) ? it.final_line_price
                                                     : it.original_line_price;
          saved += was - it.final_line_price;
          (it.line_level_discount_allocations || []).forEach(function(d){
            var t = d.discount_application && d.discount_application.title;
            if (t && titles.indexOf(t) === -1) titles.push(t);
          });
        });

        var wasSaved = host.querySelector(".atl-discount");
        if (wasSaved) wasSaved.remove();
        if (saved > 0){
          host.insertAdjacentHTML("beforeend",
            '<p class="cart__discount atl-discount atl-keep"><span>' +
            "You're saving <b>" + money(saved) + "</b>" +
            (titles.length ? " with " + esc(titles.join(" + ")) : "") +
            '</span></p>');
        }

        // ---- one price for the build --------------------------------------
        var price = host.querySelector(".cart__item__price");
        if (price) price.textContent = money(g.total);
        var linePrice = host.querySelector(".cart__price");
        if (linePrice) linePrice.textContent = money(g.total);

        // ---- one Remove ----------------------------------------------------
        var remove = host.querySelector("[data-item-remove]");
        if (remove && !remove.dataset.atlBound){
          remove.dataset.atlBound = "1";
          remove.classList.add("atl-remove");
          remove.addEventListener("click", function(e){
            /* Capture is not enough here — the theme binds its own handler
               to this anchor, so stop the event dead and do the whole
               group in one request instead of ten that race each other. */
            e.preventDefault();
            e.stopImmediatePropagation();
            var updates = {};
            g.lines.forEach(function(it){ updates[it.key] = 0; });
            fetch(ROOT + "cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({ updates: updates })
            }).then(function(){
              document.dispatchEvent(new CustomEvent("theme:cart:refresh"));
            });
          }, true);
        }
      });

      /* The count bubble. Ten lines for one folio makes the header read
         "10" when the customer has bought one thing. Every component
         beyond the folio is subtracted. */
      if (collapsed){
        document.querySelectorAll("[data-cart-count]").forEach(function(el){
          var n = parseInt(el.getAttribute("data-cart-count"), 10);
          if (!isNaN(n) && !el.dataset.atlCount){
            el.dataset.atlCount = "1";
            el.textContent = Math.max(0, n - collapsed);
          }
        });
      }

      return sweep(cart);
    }).catch(function(){ /* a broken cart is worse than an untidy one */ });
  }

  /* Orphans. Remove one line of a build from somewhere this script is not
     running — an app, a stale tab, the browser back button — and the rest
     survive without it. A component whose group has lost its folio is an
     orphan by definition and goes. */
  var sweeping = false;
  function sweep(cart){
    if (sweeping) return false;
    var updates = {}, any = false;
    groupsIn(cart).forEach(function(g){
      if (g.parent) return;

      /* ONLY AN ATELIER BUILD IS SWEPT, AND THE REASON IS THE ADD ORDER.

         The atelier adds its whole build in ONE /cart/add.js call with an
         items array. There is no instant at which components exist and
         their folio does not, so a group with no folio can only mean the
         folio was removed, and the components are rubbish.

         The off-the-shelf monogram is the opposite. The personalisation
         block adds the DEBOSSING CHARGE FIRST and then submits the folio's
         own form — it has to, because the charge failing must stop the
         folio being added at all. Between those two moments the cart
         legitimately holds a `_build` charge with no folio.

         Sweeping that window deleted a paid line in the middle of a
         normal purchase: the customer got the folio, was charged $39.95,
         and the $9.95 monogram they had asked for silently vanished.
         Found on the live site, not here.

         So a monogram charge with no folio is LEFT ALONE. It stays
         visible and priced, and the customer can remove it. A line left
         standing is a nuisance; a line deleted underneath someone is a
         wrong order. */
      if (g.kind !== "atelier") return;

      g.lines.forEach(function(it){ updates[it.key] = 0; any = true; });
    });
    if (!any) return false;
    sweeping = true;
    return fetch(ROOT + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ updates: updates })
    }).then(function(){
      sweeping = false;
      document.dispatchEvent(new CustomEvent("theme:cart:refresh"));
      return true;
    }).catch(function(){ sweeping = false; return false; });
  }

  /* The theme re-renders the cart from the server on every change, which
     throws away everything above. These are the events Broadcast already
     fires — the same set the existing add-on script in cart-drawer.liquid
     listens for — plus an observer for anything that does not announce
     itself. */
  ["cart:updated", "cart:open", "drawer:open", "theme:cart:refresh"].forEach(function(ev){
    document.addEventListener(ev, function(){ setTimeout(paint, 30); });
  });

  var pending = null;
  new MutationObserver(function(){
    clearTimeout(pending);
    pending = setTimeout(paint, 60);
  }).observe(document.body, { childList: true, subtree: true });

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", paint);
  else paint();
})(); 